/* eslint-disable @typescript-eslint/naming-convention */

import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Input,
	OnInit,
	signal,
	DestroyRef,
	inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoModule } from '@jsverse/transloco';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth, { isAccessToken } from '@okta/okta-auth-js';
import { catchError, EMPTY, tap } from 'rxjs';

import { NotificationsApiService } from '@shure/cloud/shared/notifications/notifications-data-access';
import { NotificationsStore } from '@shure/cloud/shared/notifications/notifications-store';
import { RealTimeInAppBrowserNotificationService } from '@shure/cloud/shared/services/websocket';
import {
	PrismIconButtonComponent,
	PrismBadgeComponent,
	PrismAnchorPositionComponent,
	PrismOverlayComponent
} from '@shure/prism-angular-components';

import { NotificationsPopupComponent } from './ui/components/notifications-popup/notifications-popup.component';

@Component({
	selector: 'sh-notifications-bell-icon-main',
	imports: [
		TranslocoModule,
		PrismIconButtonComponent,
		PrismBadgeComponent,
		NotificationsPopupComponent,
		PrismAnchorPositionComponent,
		PrismOverlayComponent
	],
	templateUrl: './notifications-bell-icon-main.component.html',
	styleUrl: './notifications-bell-icon-main.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationsBellIconMainComponent implements OnInit {
	private readonly notificationsApiService = inject(NotificationsApiService);
	private readonly oktaAuth = inject<OktaAuth>(OKTA_AUTH);
	private readonly notificationsStore = inject(NotificationsStore);
	private readonly realTimeInAppBrowserNotificationService = inject(RealTimeInAppBrowserNotificationService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly cdr = inject(ChangeDetectorRef);

	@Input() public application: string | undefined = '';
	@Input() public notificationsWebSocketUrl: string | undefined = '';

	public notificationsCount = signal<number>(0);
	public totalNotificationsCount = 0;
	public isNotificationsEnabled = signal(false);
	public nextPageToken?: string;

	/**
	 * Initializes notification state and subscriptions.
	 */
	public ngOnInit(): void {
		this.initializeNotifications();
	}

	/**
	 * Handles closing of the notifications popup.
	 * Resets the visibility state and forces the popup to re-mount
	 * so it can open correctly on the next bell icon click.
	 */
	public handlePopupClose(): void {
		this.isNotificationsEnabled.set(false);
	}

	/**
	 * Opens the notifications popup panel.
	 * Sets the notifications enabled state to true so the overlay becomes visible.
	 */
	public openNotifications(): void {
		this.isNotificationsEnabled.set(true);
	}

	/**
	 * Retrieves unread notification statistics and updates
	 * the unread notification count.
	 */
	public getUnReadNotifications(): void {
		this.notificationsApiService
			.getNotificationStats()
			.pipe(
				tap((notificationResponse) => {
					this.notificationsCount.set(notificationResponse.unread);
				}),
				catchError(() => EMPTY),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe();
	}

	/**
	 * Sets up notifications functionality, including subscriptions and WebSocket connection.
	 * Called when notifications are enabled.
	 */
	private initializeNotifications(): void {
		this.notificationsStore.unReadNotificationsCount$
			.pipe(
				tap((unreadNotificationsCount) => {
					this.notificationsCount.set(unreadNotificationsCount);
					this.cdr.detectChanges();
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe();
		this.getUnReadNotifications();
		this.connectWebSocket();
	}

	private connectWebSocket(): void {
		if (!this.realTimeInAppBrowserNotificationService.isWebSocketConnected()) {
			this.oktaAuth.tokenManager.get('accessToken').then((tokenData) => {
				if (isAccessToken(tokenData) && tokenData?.accessToken) {
					this.realTimeInAppBrowserNotificationService.connectWebSocket({
						application: this.application,
						token: tokenData.accessToken,
						notificationsWebSocketUrl: this.notificationsWebSocketUrl
					});
				}
			});
		}
	}
}
